'use client';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { Dispatch, SetStateAction, useState } from 'react';
import { GlassMagnifier } from 'react-image-magnifiers';

import { ZoomOutMap } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';

import { RootContainer, ImageContainer, DialogTrigger, Counter } from './Image.styles';

import { ImageDialog } from '../Dialog';
import { Text } from '../Text';

import type { ImageProps } from 'next/image';

type CustomImageProps = {
  counter?: number;
  expectedMaxWidth?: number;
  hasModal?: boolean;
  isFileBrowser?: boolean;
  isInCarousel?: boolean;
} & ImageProps;

export const Image = ({
  counter,
  expectedMaxWidth,
  hasModal,
  height,
  isFileBrowser,
  isInCarousel,
  width,
  ...rest
}: CustomImageProps) => {
  const [aspectRatio, setAspectRatio] = useState(4 / 3);

  return (
    <RootContainer $ratio={aspectRatio ?? 1} $width={expectedMaxWidth}>
      {expectedMaxWidth ? (
        <AspectRatio ratio={aspectRatio}>
          <ImageContents
            counter={counter}
            hasModal={hasModal}
            height={height}
            isFileBrowser={isFileBrowser}
            isInCarousel={isInCarousel}
            ratio={aspectRatio}
            setAspectRatio={setAspectRatio}
            width={width}
            {...rest}
          />
        </AspectRatio>
      ) : (
        <ImageContents
          counter={counter}
          hasModal={hasModal}
          height={height}
          isFileBrowser={isFileBrowser}
          isInCarousel={isInCarousel}
          ratio={aspectRatio}
          setAspectRatio={setAspectRatio}
          width={width}
          {...rest}
        />
      )}
    </RootContainer>
  );
};

type CustomImageContentProps = {
  counter?: number;
  hasModal?: boolean;
  isFileBrowser?: boolean;
  isInCarousel?: boolean;
  ratio: number;
  setAspectRatio: Dispatch<SetStateAction<number>>;
} & ImageProps;

const ImageContents = ({
  alt,
  counter,
  hasModal,
  height,
  isFileBrowser,
  isInCarousel,
  ratio,
  setAspectRatio,
  src,
  width,
  ...rest
}: CustomImageContentProps) => (
  <>
    {!isFileBrowser && (
      <ImageContainer
        alt={alt}
        height={height}
        src={src}
        width={width}
        {...rest}
        onLoad={({ currentTarget }) => setAspectRatio(currentTarget.naturalWidth / currentTarget.naturalHeight)}
        unoptimized={true}
      />
    )}
    {counter && (
      <Counter>
        <Text $variant={TextVariants.button.m}>{counter.toString().padStart(2, '0')}</Text>
      </Counter>
    )}
    {hasModal ? (
      <ImageDialog
        alt={alt}
        ratio={ratio}
        src={src.toString()}
        trigger={!isFileBrowser ? <DialogTrigger icon={<ZoomOutMap />} variant="secondary" /> : undefined}
      >
        {isFileBrowser && (
          <ImageContainer
            alt={alt}
            height={height}
            src={src}
            width={width}
            {...rest}
            onLoad={(event) => {
              setAspectRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight);

              if (rest?.onLoad) {
                rest.onLoad(event);
              }
            }}
            unoptimized={true}
          />
        )}
      </ImageDialog>
    ) : isInCarousel ? (
      <GlassMagnifier
        cursorStyle="zoom-in"
        imageAlt={alt}
        imageSrc={src.toString()}
        magnifierBorderColor={Colors.blue[50]}
        magnifierBorderSize={2}
        magnifierSize={'40%'}
        square={true}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />
    ) : (
      <ImageContainer
        alt={alt}
        height={height}
        src={src}
        width={width}
        {...rest}
        onLoad={(event) => {
          setAspectRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight);

          if (rest?.onLoad) {
            rest.onLoad(event);
          }
        }}
        unoptimized={true}
      />
    )}
  </>
);
